/* eslint-disable no-undef */
import React, { useState, useEffect } from "react";
import { Form, Input, Button, message, Card } from "antd";
import { LockOutlined, SafetyCertificateOutlined } from "@ant-design/icons";
import axiosInstance from "../utils/axiosInstance";

const Profile = () => {
  const [loading, setLoading] = useState(false);
  const [userId, setUserId] = useState(null);

  // Retrieve userId from local storage
  useEffect(() => {
    const id = localStorage.getItem("userId");
    if (id) {
      setUserId(id);
    } else {
      message.error("User ID not found. Please ensure you are logged in properly.");
    }
  }, []);

  const onFinish = async (values) => {
    const { currentPassword, newPassword, confirmPassword } = values;
    if (newPassword !== confirmPassword) {
      message.error("New passwords do not match!");
      return;
    }
    if (!userId) {
      message.error("User ID not available. Cannot update password.");
      return;
    }
    try {
      setLoading(true);
      const response = await axiosInstance.put(
        `https://dzi77xykvlyt2.cloudfront.net/api/admin/update_password/${userId}`,
        {
          curr_password: currentPassword,
          new_password: newPassword,
        }
      );
      if (response.data.success) {
        message.success("Password updated successfully!");
      } else {
        message.error(response.data.message || "Failed to update password.");
      }
    } catch (error) {
      if (error.response && error.response.data && error.response.data.message) {
        message.error(error.response.data.message);
      } else {
        message.error("Failed to update password. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-8 px-4">
      <div className="max-w-md mx-auto">
        {/* Header Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                <SafetyCertificateOutlined className="text-2xl text-blue-600" />
              </div>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Update Password</h1>
            <p className="text-gray-600">Secure your account with a new password</p>
          </div>
        </div>

        {/* Form Card */}
        <Card className="rounded-2xl shadow-sm border border-gray-200">
          <Form layout="vertical" onFinish={onFinish} className="p-1">
            <Form.Item
              label={
                <span className="text-sm font-medium text-gray-700">
                  Current Password
                </span>
              }
              name="currentPassword"
              rules={[{ required: true, message: "Please enter your current password." }]}
            >
              <Input.Password 
                placeholder="Enter current password" 
                prefix={<LockOutlined className="text-gray-400" />}
                size="large"
                className="rounded-lg h-12"
              />
            </Form.Item>
            
            <Form.Item
              label={
                <span className="text-sm font-medium text-gray-700">
                  New Password
                </span>
              }
              name="newPassword"
              rules={[{ required: true, message: "Please enter your new password." }]}
            >
              <Input.Password 
                placeholder="Enter new password" 
                prefix={<LockOutlined className="text-gray-400" />}
                size="large"
                className="rounded-lg h-12"
              />
            </Form.Item>
            
            <Form.Item
              label={
                <span className="text-sm font-medium text-gray-700">
                  Confirm New Password
                </span>
              }
              name="confirmPassword"
              rules={[{ required: true, message: "Please confirm your new password." }]}
            >
              <Input.Password 
                placeholder="Re-enter new password" 
                prefix={<LockOutlined className="text-gray-400" />}
                size="large"
                className="rounded-lg h-12"
              />
            </Form.Item>

            {/* Password Requirements */}
            <div className="bg-blue-50 rounded-lg p-4 mb-6 border border-blue-200">
              <h4 className="text-sm font-semibold text-blue-800 mb-2 flex items-center">
                <SafetyCertificateOutlined className="mr-2" />
                Password Requirements
              </h4>
              <ul className="text-xs text-blue-700 space-y-1">
                <li>• At least 8 characters long</li>
                <li>• One uppercase letter (A-Z)</li>
                <li>• One lowercase letter (a-z)</li>
                <li>• One number (0-9)</li>
                <li>• One special character (!@#$%^&*)</li>
              </ul>
            </div>

            <Form.Item className="mb-0">
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={loading}
                size="large"
                className="w-full h-12 rounded-lg bg-blue-600 hover:bg-blue-700 border-blue-600 text-white font-semibold text-base"
              >
                {loading ? "Updating Password..." : "Update Password"}
              </Button>
            </Form.Item>
          </Form>
        </Card>

        {/* Security Note */}
        <div className="text-center mt-6">
          <p className="text-xs text-gray-500">
            For security reasons, please log out and log back in after changing your password.
          </p>
        </div>
      </div>

      <style jsx>{`
        .ant-card .ant-card-body {
          padding: 24px;
        }
        .ant-form-item-label > label {
          font-size: 14px;
          font-weight: 500;
        }
        .ant-input-affix-wrapper {
          border-radius: 12px;
          border: 1px solid #d1d5db;
          transition: all 0.2s ease;
        }
        .ant-input-affix-wrapper:hover,
        .ant-input-affix-wrapper:focus {
          border-color: #3b82f6;
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
        }
      `}</style>
    </div>
  );
};

export default Profile;